import axios, { type AxiosInstance } from "axios";
import pLimit from "p-limit";

/**
 * Axios instances that already have a concurrency limit.
 */
const instancesWithConcurrencyLimit = new WeakSet<AxiosInstance>();

/**
 * Configures concurrent-request limiting for an axios instance.
 *
 * @remarks
 * - Wraps the instance's adapter, so a request holds a slot only while it is in flight.
 * - Call **once** per instance (done by {@link AjaxConfigRegistration} in `created()`), after its
 *   adapter is final. A second call throws.
 * - Replacing `defaults.adapter` later, or passing `adapter` per request, bypasses the limit.
 * - Instances created with `axios.create()` after this call draw from the same pool of slots;
 *   instances created before it are not limited at all.
 * - A request aborted while queued keeps its place until it gets a slot.
 * - Disabling the limiter is handled at the call site.
 *
 * @param concurrencyLimit
 * Maximum number of concurrent in-flight requests. Must be `>= 1`.
 *
 * @param axiosInstance
 * The axios instance to configure. Defaults to the shared singleton instance.
 */
export function setupConcurrencyLimit(concurrencyLimit: number, axiosInstance: AxiosInstance = axios): void {
	if (concurrencyLimit < 1) {
		throw new Error("concurrencyLimit must be greater than 0");
	}

	if (instancesWithConcurrencyLimit.has(axiosInstance)) {
		throw new Error("setupConcurrencyLimit was already called for this axios instance");
	}
	instancesWithConcurrencyLimit.add(axiosInstance);

	const limiter = pLimit(concurrencyLimit);
	const sendRequest = axios.getAdapter(axiosInstance.defaults.adapter);

	// p-limit frees the slot when sendRequest settles (success, error, timeout, abort),
	// before axios runs transformResponse or any response interceptor, so no explicit release is needed.
	axiosInstance.defaults.adapter = (config) => config.skipConcurrencyLimiter === true
		? sendRequest(config)
		: limiter(() => sendRequest(config));
};
